import { useFormContext, useWatch } from "react-hook-form";
import { useUpdateEffect } from "react-use";
import { useCreateObservationQuestion } from "src/features/observationsAdmin/hooks";
import usePatchObservationQuestion from "src/features/observationsAdmin/hooks/usePatchObservationQuestion/usePatchObservationQuestion";
import useUpdateObservationQuestion from "src/features/observationsAdmin/hooks/usePutObservationQuestion/usePutObservationQuestion";
import { getQuestionIdByType } from "src/utils/getQuestionType";

type UseAdminUpsertQuestionOptions = {
	questionIndex: number;
};

const useAdminUpsertQuestion = ({
	questionIndex
}: UseAdminUpsertQuestionOptions) => {
	const { control, setValue } = useFormContext<AdminObservationForm>();

	const question = useWatch({
		control,
		name: `questions.${questionIndex}`
	});

	const observationFormId = useWatch({
		control,
		name: "observationFormId"
	});

	const { mutate: updateObservationQuestion, isLoading: isUpdatingQuestion } =
		useUpdateObservationQuestion();

	const { mutate: createObservationQuestion, isLoading: isSubmittingQuestion } =
		useCreateObservationQuestion();

	const { mutate: patchObservationQuestion } = usePatchObservationQuestion();

	const isActionRequired = useWatch({
		control,
		name: `questions.${questionIndex}.isActionRequired`
	});

	const isCorrectAnswerRequired = useWatch({
		control,
		name: `questions.${questionIndex}.isCorrectAnswerRequired`
	});

	const onSuccess = ({ id }: IAdminQuestion) => {
		setValue(`questions.${questionIndex}.id`, id);
	};

	// When the isActionRequired or isCorrectAnswerRequired setting checkbox changes, patch the question or create it
	useUpdateEffect(() => {
		const questionId = question.id;

		if (questionId) {
			return patchObservationQuestion({
				id: questionId,
				is_action_required: Boolean(isActionRequired),
				is_correct_answer_required: Boolean(isCorrectAnswerRequired)
			});
		}

		createObservationQuestion(
			{
				observation_form_id: observationFormId,
				question_type_id:
					question.questionTypeId ??
					getQuestionIdByType(
						question.type?.toUpperCase() as IQuestionTypeEnum
					),
				order: question.order,
				question: question.question,
				note: question.note,
				is_correct_answer_required: Boolean(question.isCorrectAnswerRequired)
			},
			{ onSuccess }
		);
	}, [isActionRequired, isCorrectAnswerRequired]);

	// On blur, either update or create the question
	const upsertQuestion = async () => {
		if (question.id) {
			updateObservationQuestion({
				id: question.id,
				observation_form_id: observationFormId,
				question_type_id:
					question.questionTypeId ??
					getQuestionIdByType(
						question.type?.toUpperCase() as IQuestionTypeEnum
					),
				question: question.question,
				note: question.note,
				order: question.order
			});

			return;
		}

		createObservationQuestion(
			{
				observation_form_id: observationFormId,
				question_type_id:
					question.questionTypeId ??
					getQuestionIdByType(
						question.type?.toUpperCase() as IQuestionTypeEnum
					),
				order: question.order,
				question: question.question,
				note: question.note,
				branch_type: "none"
			},
			{ onSuccess }
		);
	};

	return { upsertQuestion, isSubmittingQuestion, isUpdatingQuestion };
};

export default useAdminUpsertQuestion;
